import { useState, useEffect, useCallback } from 'react';
import { platformFetch } from '../../../platform/http';
import { formatBeijingDate, formatBeijingDateTime } from '../../../utils/time';
import {
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Eye,
  ShoppingBag,
  Loader2,
  Filter,
} from 'lucide-react';

interface EcommerceTask {
  id: string;
  title: string;
  userId: string;
  userEmail: string;
  userName: string;
  productDescription: string;
  nodeCount: number;
  createdAt: string;
  updatedAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const nodeStatusMap: Record<string, { label: string; color: string; bg: string }> = {
  idle: { label: '待生成', color: 'text-gray-600', bg: 'bg-gray-50' },
  generating: { label: '生成中', color: 'text-blue-600', bg: 'bg-blue-50' },
  success: { label: '成功', color: 'text-green-600', bg: 'bg-green-50' },
  failed: { label: '失败', color: 'text-red-600', bg: 'bg-red-50' },
};

export default function EcommerceTasksTab() {
  const [tasks, setTasks] = useState<EcommerceTask[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [userEmailFilter, setUserEmailFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [detailTask, setDetailTask] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchTasks = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (userEmailFilter) params.set('userEmail', userEmailFilter);
      if (statusFilter) params.set('status', statusFilter);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);

      const res = await platformFetch(`/admin/ecommerce-replicate/tasks?${params}`);
      const data = await res.json();
      if (data.data) {
        setTasks(data.data);
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error('获取复刻任务失败:', err);
    } finally {
      setLoading(false);
    }
  }, [userEmailFilter, statusFilter, startDate, endDate]);

  useEffect(() => { fetchTasks(1); }, [fetchTasks]);

  const handleDelete = async (id: string) => {
    if (!confirm('确定删除该复刻任务？此操作不可恢复。')) return;
    try {
      const res = await platformFetch(`/admin/ecommerce-replicate/tasks/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        fetchTasks(pagination.page);
      } else {
        const data = await res.json();
        alert(data.error || '删除失败');
      }
    } catch {
      alert('删除失败');
    }
  };

  const handleViewDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const res = await platformFetch(`/admin/ecommerce-replicate/tasks/${id}`);
      const data = await res.json();
      if (data.data) setDetailTask(data.data);
    } catch {
      alert('获取详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSearch = () => fetchTasks(1);
  const handleReset = () => {
    setUserEmailFilter('');
    setStatusFilter('');
    setStartDate('');
    setEndDate('');
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* 搜索和筛选 */}
      <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex-1 min-w-0">
            <label className="text-sm text-light-muted mb-1 block">用户邮箱</label>
            <input type="text" placeholder="按邮箱筛选" value={userEmailFilter}
              onChange={(e) => setUserEmailFilter(e.target.value)}
              className="w-full px-4 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20" />
          </div>
          <div className="w-full lg:w-40">
            <label className="text-sm text-light-muted mb-1 block">节点状态</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20">
              <option value="">全部状态</option>
              <option value="idle">待生成</option>
              <option value="generating">生成中</option>
              <option value="success">成功</option>
              <option value="failed">失败</option>
            </select>
          </div>
          <div className="w-full lg:w-44">
            <label className="text-sm text-light-muted mb-1 block">开始日期</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20" />
          </div>
          <div className="w-full lg:w-44">
            <label className="text-sm text-light-muted mb-1 block">结束日期</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20" />
          </div>
          <div className="flex gap-2">
            <button onClick={handleSearch}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-light-primary text-white rounded-xl hover:bg-light-primary/90 transition-colors">
              <Filter className="w-4 h-4" /><span className="hidden sm:inline">筛选</span>
            </button>
            <button onClick={handleReset}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-light-hover text-light-text rounded-xl hover:bg-light-border transition-colors">
              <RefreshCw className="w-4 h-4" /><span className="hidden sm:inline">重置</span>
            </button>
          </div>
        </div>
      </div>

      {/* 任务列表 */}
      <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-light-border bg-light-hover">
                <th className="text-left text-sm font-medium text-light-muted py-3 px-4">任务标题</th>
                <th className="text-left text-sm font-medium text-light-muted py-3 px-4">用户</th>
                <th className="text-center text-sm font-medium text-light-muted py-3 px-4">节点数</th>
                <th className="text-left text-sm font-medium text-light-muted py-3 px-4">商品描述</th>
                <th className="text-left text-sm font-medium text-light-muted py-3 px-4">创建时间</th>
                <th className="text-center text-sm font-medium text-light-muted py-3 px-4">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto" /></td></tr>
              ) : tasks.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-light-muted">
                  <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>暂无复刻任务</p>
                </td></tr>
              ) : tasks.map((t) => (
                <tr key={t.id} className="border-b border-light-border last:border-0 hover:bg-light-hover/50">
                  <td className="py-3 px-4"><p className="text-sm text-light-text font-medium">{t.title || '未命名任务'}</p></td>
                  <td className="py-3 px-4">
                    <p className="text-sm text-light-text">{t.userEmail}</p>
                    {t.userName && <p className="text-xs text-light-muted">{t.userName}</p>}
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-light-text">{t.nodeCount}</td>
                  <td className="py-3 px-4"><p className="text-sm text-light-muted max-w-xs truncate">{t.productDescription || '-'}</p></td>
                  <td className="py-3 px-4 text-sm text-light-muted">{formatBeijingDateTime(t.createdAt)}</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => handleViewDetail(t.id)} className="p-2 hover:bg-light-hover rounded-lg transition-colors" title="查看详情">
                        <Eye className="w-4 h-4 text-light-muted" />
                      </button>
                      <button onClick={() => handleDelete(t.id)} className="p-2 hover:bg-red-50 rounded-lg transition-colors" title="删除">
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden divide-y divide-light-border">
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto" /></div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-12 text-light-muted">
              <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>暂无复刻任务</p>
            </div>
          ) : tasks.map((t) => (
            <div key={t.id} className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-light-text">{t.title || '未命名任务'}</p>
                  <p className="text-xs text-light-muted">{t.userEmail}</p>
                </div>
                <div className="flex gap-1 ml-2">
                  <button onClick={() => handleViewDetail(t.id)} className="p-1.5 hover:bg-light-hover rounded-lg"><Eye className="w-4 h-4 text-light-muted" /></button>
                  <button onClick={() => handleDelete(t.id)} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-light-hover rounded-lg p-2 text-center">
                  <p className="text-xs text-light-muted">节点数</p>
                  <p className="text-sm font-medium text-light-text">{t.nodeCount}</p>
                </div>
                <div className="bg-light-hover rounded-lg p-2 text-center">
                  <p className="text-xs text-light-muted">创建时间</p>
                  <p className="text-xs text-light-text">{formatBeijingDate(t.createdAt)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 分页 */}
        {pagination.totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between px-4 lg:px-6 py-4 border-t border-light-border gap-3">
            <p className="text-sm text-light-muted">共 {pagination.total} 条，第 {pagination.page} / {pagination.totalPages} 页</p>
            <div className="flex items-center gap-2">
              <button onClick={() => fetchTasks(pagination.page - 1)} disabled={pagination.page <= 1}
                className="flex items-center gap-1 px-3 py-2 rounded-xl border border-light-border hover:bg-light-hover text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                <ChevronLeft className="w-4 h-4" /><span className="hidden sm:inline">上一页</span>
              </button>
              <button onClick={() => fetchTasks(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages}
                className="flex items-center gap-1 px-3 py-2 rounded-xl border border-light-border hover:bg-light-hover text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                <span className="hidden sm:inline">下一页</span><ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 详情弹窗 */}
      {detailTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-light-border">
              <div>
                <h3 className="text-lg font-semibold text-light-text">复刻任务详情</h3>
                <p className="text-sm text-light-muted mt-1">{detailTask.title || '未命名任务'}</p>
              </div>
              <button onClick={() => setDetailTask(null)} className="p-2 hover:bg-light-hover rounded-lg transition-colors">
                <span className="text-light-muted text-lg">&times;</span>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 lg:p-6 space-y-4">
              {detailLoading ? (
                <div className="text-center py-8"><Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto" /></div>
              ) : (
                <>
                  <div className="bg-light-hover rounded-xl p-4">
                    <h4 className="text-sm font-medium text-light-muted mb-3">基本信息</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div><p className="text-xs text-light-muted">用户</p><p className="text-sm text-light-text">{detailTask.user?.email}</p></div>
                      <div><p className="text-xs text-light-muted">创建时间</p><p className="text-sm text-light-text">{formatBeijingDateTime(detailTask.createdAt)}</p></div>
                      <div className="col-span-2"><p className="text-xs text-light-muted">商品描述</p><p className="text-sm text-light-text">{detailTask.productDescription || '-'}</p></div>
                    </div>
                  </div>
                  <div className="bg-light-hover rounded-xl p-4">
                    <h4 className="text-sm font-medium text-light-muted mb-3">素材节点 ({detailTask.nodes?.length || 0})</h4>
                    <div className="space-y-2 max-h-60 overflow-auto">
                      {(detailTask.nodes || []).map((node: any) => (
                        <div key={node.id} className="flex items-center gap-3 bg-white rounded-lg p-3 text-sm">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${nodeStatusMap[node.status]?.bg} ${nodeStatusMap[node.status]?.color}`}>
                            {nodeStatusMap[node.status]?.label || node.status}
                          </span>
                          <span className="text-light-text flex-1 truncate">{node.title || node.prompt?.slice(0, 50) || '未命名节点'}</span>
                          <span className="text-light-muted text-xs">{node.model || '-'}</span>
                        </div>
                      ))}
                      {(!detailTask.nodes || detailTask.nodes.length === 0) && <p className="text-sm text-light-muted text-center py-4">暂无节点</p>}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
